import { Upload } from 'lucide-react';
import React, { ChangeEvent, useRef } from 'react';
import { showToastNotification } from '@/utility/helper';
import ProgressBar from '../../UI/loaders/Progressbar';

interface InputFieldProps {
  label?: string;
  helperText?: string;
  multiple?: boolean;
  onFilesChange?: (files: FileList | null) => void;
  isLoading?: boolean;
  accept?: string;
  disable?: boolean;
  folderUpload?: boolean;
  progress?: number
}

const MediaInputField: React.FC<InputFieldProps> = ({
  label = 'Click to upload',
  helperText,
  multiple = false,
  onFilesChange,
  isLoading = false,
  accept = '*/*',
  disable = false,
  folderUpload = false,
  progress = 0
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onFilesChange) return;

    const files = e.target.files;
    if (!files) return;

    const acceptType = accept || '*/*';

    const validFiles = Array.from(files).filter(file => {
      if (acceptType === 'video/*') return file.type.startsWith('video/');
      if (acceptType === 'image/*') return file.type.startsWith('image/');
      return true; // fallback
    });
    console.log('accept type0', acceptType)
    if (validFiles.length !== files.length) {
      const message =
        acceptType === 'video/*'
          ? 'Only video files are allowed.'
          : acceptType === 'image/*'
            ? 'Only image files are allowed.'
            : 'Invalid file type.';
      showToastNotification('info', message);
    }

    onFilesChange(validFiles.length ? new DataTransfer().files : null);

    const dataTransfer = new DataTransfer();
    validFiles.forEach(file => dataTransfer.items.add(file));
    onFilesChange(dataTransfer.files);
  };


  const openFilePicker = () => {
    if (!fileInputRef.current || disable) return;
    fileInputRef.current.value = ''; // reset previous selection
    fileInputRef.current.click();
  };

  const openFolderPicker = () => {
    if (!folderInputRef.current || disable) return;
    folderInputRef.current.value = ''; // reset previous selection
    folderInputRef.current.click();
  };

  return (
    <>
      <div
        className={`border border-primary border-dashed rounded-2xl p-4 sm:p-6 text-center hover:border-blue-400 transition block ${disable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
      >
        {isLoading ? (
          <div>
            <ProgressBar
              count={progress}
            />
          </div>
        ) : (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple={multiple}
              onChange={handleChange}
              accept={accept}
              disabled={disable}
            />

            {folderUpload && (
              <input
                ref={folderInputRef}
                type="file"
                className="hidden"
                // @ts-ignore
                webkitdirectory="true"
                onChange={handleChange}
                disabled={disable}
              />
            )}

            <span className="inline-block mb-2">
              <Upload color="#2D73B4" />
            </span>

            <p className="text-primary mb-2 font-normal">{label}</p>

            <div className="flex justify-center gap-2 text-sm">
              <button
                type="button"
                onClick={openFilePicker}
                disabled={disable}
                className="px-3 py-1 border rounded text-blue-600 hover:bg-blue-50"
              >
                Select File
              </button>

              {folderUpload && (
                <button
                  type="button"
                  onClick={openFolderPicker}
                  disabled={disable}
                  className="px-3 py-1 border rounded text-green-600 hover:bg-green-50"
                >
                  Select Folder
                </button>
              )}
            </div>

            {helperText && (
              <p className="text-xs text-gray-500 mt-2">{helperText}</p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default MediaInputField;
